// src/components/LoginPage.js
// Premium auth drop-in
// - Keeps staged credentials/code flow
// - Keeps provider MFA flow
// - Keeps syncAuthUserToFirestore
// - Preserves redirect with full path + query
// - Cleans duplicate redirect logic
// - Adds premium-friendly structure/classes for CSS styling
// - Adds phone OTP support via Firebase Authentication
// - Sends first-time marketplace users to /preferences until preferencesCompleted === true

import React, { useState, useEffect, useMemo, useRef } from "react";
import { auth, googleProvider, facebookProvider } from "../firebase";
import { Api } from "../lib/api";
import "./LoginPage.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import firebase from "firebase/compat/app";
import AlertToast from "../AlertToast";
import { syncAuthUserToFirestore } from "../lib/syncAuthUserToFirestore";

import useAccessProfile from "../hooks/useAccessProfile";
import { hasPermission, isActiveUser } from "../utils/accessControl";

// ✅ Feature flags for login options
const ENABLE_FACEBOOK_LOGIN =
  process.env.REACT_APP_ENABLE_FACEBOOK_LOGIN === "true";
const ENABLE_PHONE_LOGIN =
  process.env.REACT_APP_ENABLE_PHONE_LOGIN !== "false";

// Phone OTP requires Firebase Authentication Phone provider to be enabled in Firebase Console.
const MFA_PENDING_KEY = "nx_mfa_pending";
const MFA_TXID_KEY = "nx_mfa_txid";
const MFA_VIA_KEY = "nx_mfa_via";

const LoginPage = ({ setCurrentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAccessProfile();

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const [stage, setStage] = useState("credentials"); // "credentials" | "code"
  const [codeVia, setCodeVia] = useState(null); // "email" | "phone" | null
  const [mfaTxId, setMfaTxId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  // ✅ Phone OTP state
  const [phoneMode, setPhoneMode] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const recaptchaVerifierRef = useRef(null);

  const RESEND_SECONDS = 60;
  const [resendCountdown, setResendCountdown] = useState(0);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  const friendlyError = (err) => {
    const raw = String(err?.message || err?.error || "").toLowerCase();
    const status = err?.status || err?.response?.status || err?.code;

    if (raw.includes("expired") || raw.includes("timeout") || raw.includes("too old")) {
      return "Your verification code has expired. Please request a new one and try again.";
    }

    if (
      raw.includes("invalid code") ||
      (raw.includes("invalid") && raw.includes("code")) ||
      raw.includes("wrong code")
    ) {
      return "That verification code is not correct. Please check it and try again.";
    }

    if (raw.includes("too many") || raw.includes("rate") || status === 429) {
      return "Too many attempts were detected. Please wait a moment and try again.";
    }

    if (raw.includes("network") || raw.includes("failed to fetch") || err?.isNetworkError) {
      return "We could not reach the server. Please check your internet connection and try again.";
    }

    if (
      raw.includes("user-not-found") ||
      raw.includes("invalid login credentials") ||
      raw.includes("wrong-password") ||
      raw.includes("invalid password") ||
      status === 401
    ) {
      return "Your login details are incorrect. Please check them and try again.";
    }

    if (raw.includes("user-disabled")) {
      return "This account has been disabled. Please contact support if you think this is a mistake.";
    }

    if (raw.includes("invalid-custom-token")) {
      return "Your login session could not be completed. Please request a new verification code and try again.";
    }

    if (raw.includes("custom-token-mismatch")) {
      return "Your login session is no longer valid. Please start again and request a new code.";
    }

    if (status === 403) {
      return "You do not have permission to access this account.";
    }

    if (status === 428) {
      return "Verification is still required before you can continue.";
    }

    if (raw.includes("must sign in using a nexterzone email")) {
      return "Admins must sign in with their NexterZone email address.";
    }

    if (raw.includes("valid email address or phone number")) {
      return "Please enter a valid email address or phone number.";
    }

    if (raw.includes("6-digit")) {
      return "Please enter the 6-digit verification code.";
    }

    if (raw.includes("verification session expired")) {
      return "Your verification session has expired. Please request a new code.";
    }

    if (raw.includes("unable to start verification")) {
      return "We could not start verification right now. Please try again.";
    }

    if (raw.includes("verification failed")) {
      return "We could not verify your code. Please try again.";
    }

    if (raw.includes("password reset")) {
      return "We could not send the password reset email right now. Please try again.";
    }

    return "Something went wrong. Please try again.";
  };

  const detectMode = (input) => {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)) return "email";
    if (/^\d{7,13}$/.test(input) || /^\+\d{10,15}$/.test(input)) return "phone";
    return "unknown";
  };

  const normalizePhone = (input) => {
    let phone = (input || "").trim();
    if (!phone) return "";
    if (!phone.startsWith("+")) {
      phone = phone.startsWith("0") ? `+27${phone.slice(1)}` : `+27${phone}`;
    }
    return phone;
  };

  // ✅ Phone OTP normalization helper
  const normalizePhoneForOtp = (value) => {
    const raw = String(value || "").trim();

    if (!raw) return "";

    // If already international, keep it but remove spaces/hyphens.
    if (raw.startsWith("+")) {
      return raw.replace(/[^\d+]/g, "");
    }

    // South Africa default:
    // 0821234567 -> +27821234567
    const digits = raw.replace(/\D/g, "");

    if (digits.startsWith("0") && digits.length === 10) {
      return `+27${digits.slice(1)}`;
    }

    if (digits.startsWith("27")) {
      return `+${digits}`;
    }

    return `+${digits}`;
  };

  const formatPhoneForDisplay = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 10);

    // South African mobile display format: 082 123 4567
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;

    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  };

  const clearOtpState = () => {
    setCode("");
    setMfaTxId(null);
    setCodeVia(null);
  };

  const clearMfaSessionStorage = () => {
    try {
      sessionStorage.removeItem(MFA_PENDING_KEY);
      sessionStorage.removeItem(MFA_TXID_KEY);
      sessionStorage.removeItem(MFA_VIA_KEY);
    } catch {}
  };

  const waitForFirebaseUser = (timeoutMs = 8000) =>
    new Promise((resolve, reject) => {
      if (auth.currentUser?.uid) return resolve(auth.currentUser);

      let done = false;

      const timer = setTimeout(() => {
        if (done) return;
        done = true;
        try {
          unsub && unsub();
        } catch {}
        reject(new Error("Firebase session not established."));
      }, timeoutMs);

      const unsub = auth.onAuthStateChanged((u) => {
        if (done) return;
        if (u?.uid) {
          done = true;
          clearTimeout(timer);
          try {
            unsub();
          } catch {}
          resolve(u);
        }
      });
    });

  const signInWithServerCustomToken = async (customToken) => {
    await auth.setPersistence(
      rememberMe
        ? firebase.auth.Auth.Persistence.LOCAL
        : firebase.auth.Auth.Persistence.SESSION
    );

    await auth.signOut().catch(() => {});
    clearMfaSessionStorage();

    await auth.signInWithCustomToken(customToken);

    const fbUser = await waitForFirebaseUser();
    await fbUser.reload().catch(() => {});
    return fbUser;
  };

  const pickBoolean = (...values) =>
    values.find((value) => typeof value === "boolean");

  const getPreferencesCompleted = (me, fallbackProfile) => {
    const value = pickBoolean(
      me?.preferencesCompleted,
      me?.user?.preferencesCompleted,
      me?.profile?.preferencesCompleted,
      me?.accessProfile?.preferencesCompleted,
      me?.onboarding?.preferencesCompleted,
      me?.user?.onboarding?.preferencesCompleted,
      me?.profile?.onboarding?.preferencesCompleted,
      me?.accessProfile?.onboarding?.preferencesCompleted,
      fallbackProfile?.preferencesCompleted,
      fallbackProfile?.onboarding?.preferencesCompleted
    );

    // Only true means completed.
    // Missing, undefined, null, or false means the user still needs preferences.
    return value === true;
  };

  const handleAfterLogin = (knownUser = null, me = null, syncedProfile = null) => {
    const active = isActiveUser(profile);
    const canInspect = active && hasPermission(profile, "inspectionReview");
    const canManageRoles = active && hasPermission(profile, "roleManagement");

    const email = String(knownUser?.email || auth.currentUser?.email || "")
      .trim()
      .toLowerCase();

    const isNzStaff = !!email && email.endsWith("@nexterzone.com");

    const preferencesCompleted = getPreferencesCompleted(
      me,
      syncedProfile || profile
    );

    const shouldGoToPreferences =
      !isNzStaff &&
      !canInspect &&
      !canManageRoles &&
      !preferencesCompleted;

    if (shouldGoToPreferences) {
      localStorage.removeItem("postLoginRedirect");

      if (!location.pathname.toLowerCase().startsWith("/preferences")) {
        navigate("/preferences", { replace: true });
      }

      return;
    }

    const resolveSafeTarget = (rawTarget) => {
      const target = String(rawTarget || "").trim();
      if (!target) return "";

      const lowerTarget = target.toLowerCase();

      if (
        lowerTarget === "/" ||
        lowerTarget.startsWith("/login") ||
        lowerTarget.startsWith("/register") ||
        lowerTarget.startsWith("/unauthorized")
      ) {
        return "";
      }

      if (
        lowerTarget.startsWith("/admindashboard") ||
        lowerTarget.startsWith("/admin/tools") ||
        lowerTarget.startsWith("/admin/user-access")
      ) {
        return canManageRoles || isNzStaff ? target : "/";
      }

      if (lowerTarget.startsWith("/inspection")) {
        return canInspect || isNzStaff ? target : "/";
      }

      return target;
    };

    const rawFrom = location.state?.from;
    let from = "";

    if (typeof rawFrom === "string" && rawFrom.trim()) {
      from = rawFrom;
    } else if (rawFrom && typeof rawFrom === "object" && rawFrom.pathname) {
      from = rawFrom.pathname + (rawFrom.search || "");
    } else {
      const stored = localStorage.getItem("postLoginRedirect");
      if (stored && typeof stored === "string" && stored.trim()) {
        from = stored;
        localStorage.removeItem("postLoginRedirect");
      }
    }

    const safeTarget = resolveSafeTarget(from);

    if (safeTarget) {
      navigate(safeTarget, { replace: true });
      return;
    }

    if (canInspect || isNzStaff) {
      navigate("/inspection", { replace: true });
      return;
    }

    navigate("/", { replace: true });
  };

  const afterAuthSuccess = async (knownUser = null) => {
    localStorage.setItem("rememberMe", rememberMe ? "1" : "0");
    clearMfaSessionStorage();

    const fbUser = knownUser?.uid
      ? knownUser
      : auth.currentUser?.uid
      ? auth.currentUser
      : await waitForFirebaseUser(8000);

    if (!fbUser?.uid) {
      throw new Error("Firebase session not established.");
    }

    await fbUser.reload?.().catch(() => {});

    let syncedProfile = null;

    try {
      syncedProfile = await syncAuthUserToFirestore(fbUser);
    } catch (err) {
      console.error("Failed to sync auth user to Firestore:", err);
      throw err;
    }

    let me = null;

    try {
      me = await Api.meOptional();
    } catch {}

    if (typeof setCurrentUser === "function") {
      setCurrentUser(fbUser);
    }

    showToast("Welcome back. You have logged in successfully.", "success");
    handleAfterLogin(fbUser, me, syncedProfile);
  };

  useEffect(() => {
    const saved = localStorage.getItem("rememberMe") === "1";
    setRememberMe(saved);
  }, []);

  useEffect(() => {
    if (!location.state?.from) {
      const ref = document.referrer;
      const sameOrigin = ref && ref.startsWith(window.location.origin);

      if (sameOrigin) {
        const path = ref.replace(window.location.origin, "");
        const cleanPath = path.split("?")[0].toLowerCase();

        const blockedRedirects = new Set([
          "/",
          "/login",
          "/register",
          "/preferences",
          "/unauthorized",
        ]);

        if (
          path &&
          path.startsWith("/") &&
          !blockedRedirects.has(cleanPath)
        ) {
          localStorage.setItem("postLoginRedirect", path);
        }
      }
    }
  }, [location.state]);

  useEffect(() => {
    return () => {
      try {
        recaptchaVerifierRef.current?.clear?.();
      } catch {}
      recaptchaVerifierRef.current = null;
    };
  }, []);

  // ✅ Phone OTP handlers
  const handlePhoneOtpClick = () => {
    setPhoneMode(true);
    setPhoneNumber("");
    setOtpCode("");
    setPhoneError("");
    setConfirmationResult(null);
  };

  const handlePhoneBackToEmail = () => {
    setPhoneMode(false);
    setPhoneNumber("");
    setOtpCode("");
    setPhoneError("");
    setConfirmationResult(null);
    try {
      recaptchaVerifierRef.current?.clear?.();
    } catch {}
    recaptchaVerifierRef.current = null;
  };

  const handleSendPhoneOtp = async (e) => {
    e.preventDefault();
    setPhoneBusy(true);
    setPhoneError("");

    try {
      const normalized = normalizePhoneForOtp(phoneNumber);

      if (!/^\+\d{10,15}$/.test(normalized)) {
        setPhoneError("Please enter a valid phone number.");
        return;
      }

      if (!recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current = new firebase.auth.RecaptchaVerifier(
          "phone-recaptcha-container",
          {
            size: "invisible",
            callback: () => {},
          }
        );
      }

      await auth.setPersistence(
        rememberMe
          ? firebase.auth.Auth.Persistence.LOCAL
          : firebase.auth.Auth.Persistence.SESSION
      );

      const result = await auth.signInWithPhoneNumber(
        normalized,
        recaptchaVerifierRef.current
      );

      setConfirmationResult(result);
      setPhoneError("");
      showToast("OTP sent. Please check your phone.", "success");
    } catch (err) {
      console.error("Phone OTP error:", err);
      const msg = String(err?.message || "").toLowerCase();

      if (msg.includes("too-many-requests") || msg.includes("too many")) {
        setPhoneError("Too many attempts. Please wait a few minutes and try again.");
      } else if (msg.includes("invalid-phone-number")) {
        setPhoneError("Please enter a valid phone number.");
      } else if (msg.includes("network")) {
        setPhoneError("Network error. Please check your connection and try again.");
      } else {
        setPhoneError("Failed to send OTP. Please try again.");
      }

      try {
        recaptchaVerifierRef.current?.clear?.();
      } catch {}
      recaptchaVerifierRef.current = null;
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleVerifyPhoneOtp = async (e) => {
    e.preventDefault();
    setPhoneBusy(true);
    setPhoneError("");

    try {
      if (!confirmationResult) {
        throw new Error("OTP session expired. Please request a new code.");
      }

      if (!/^\d{6}$/.test(otpCode.trim())) {
        setPhoneError("Please enter a valid 6-digit code.");
        return;
      }

      const result = await confirmationResult.confirm(otpCode.trim());
      const fbUser = result?.user;

      if (!fbUser?.uid) {
        throw new Error("Phone verification did not complete.");
      }

      setPhoneMode(false);
      setPhoneNumber("");
      setOtpCode("");
      setConfirmationResult(null);
      try {
        recaptchaVerifierRef.current?.clear?.();
      } catch {}
      recaptchaVerifierRef.current = null;

      await afterAuthSuccess(fbUser);
    } catch (err) {
      console.error("Phone verify error:", err?.code, err?.message, err);
      const msg = String(err?.message || "").toLowerCase();

      if (msg.includes("invalid-verification-code")) {
        setPhoneError("The verification code is incorrect or expired.");
      } else if (msg.includes("code-expired")) {
        setPhoneError("The verification code has expired. Please request a new one.");
      } else if (msg.includes("too many")) {
        setPhoneError("Too many attempts. Please wait and try again.");
      } else {
        setPhoneError("Phone verification failed. Please try again.");
      }
    } finally {
      setPhoneBusy(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const result = await auth.getRedirectResult();
        const fbUser = result?.user || null;
        if (!fbUser) return;

        try {
          sessionStorage.setItem(MFA_PENDING_KEY, "1");
        } catch {}

        const idToken = await fbUser.getIdToken(true);
        await auth.signOut().catch(() => {});

        const started = await Api.socialStartMfa({ idToken });
        const txId = started?.mfa?.txId || started?.txId || null;
        const via = started?.mfa?.via || started?.via || null;

        if (!txId || !via) {
          throw new Error("Unable to start verification.");
        }

        if (!mounted) return;

        try {
          sessionStorage.setItem(MFA_TXID_KEY, txId);
          sessionStorage.setItem(MFA_VIA_KEY, via);
        } catch {}

        setMfaTxId(txId);
        setCodeVia(via === "phone" ? "phone" : "email");
        setStage("code");
        startResendTimer();

        showToast(
          via === "phone"
            ? "We sent a verification code to your phone."
            : "We sent a verification code to your email.",
          "success"
        );
      } catch {
        clearMfaSessionStorage();
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (stage !== "code" || resendCountdown <= 0) return;

    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [stage, resendCountdown]);

  const handleForgotPassword = async () => {
    const raw = (emailOrPhone || "").trim();
    const mode = detectMode(raw);

    if (mode !== "email") {
      showToast(
        "Please enter your email address first to receive a password reset link.",
        "error"
      );
      return;
    }

    setResettingPassword(true);

    try {
      await auth.sendPasswordResetEmail(raw.toLowerCase());
      showToast(
        "If that email address is registered, a password reset link has been sent.",
        "success"
      );
    } catch (err) {
      console.error(err);
      showToast(
        "We could not send the reset email right now. Please check the email address and try again.",
        "error"
      );
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSubmitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);

    const raw = (emailOrPhone || "").trim();
    const mode = detectMode(raw);
    const rawLower = raw.toLowerCase();
    const looksAdmin = rawLower.endsWith("@nexterzone.com");

    try {
      if (looksAdmin && mode !== "email") {
        throw new Error("Admins must sign in using a NexterZone email address.");
      }

      if (mode === "unknown") {
        throw new Error("Please enter a valid email address or phone number.");
      }

      if (password.trim()) {
        const payload =
          mode === "email"
            ? { email: raw.toLowerCase(), password: password.trim(), rememberMe }
            : { phone: normalizePhone(raw), password: password.trim(), rememberMe };

        const r = await Api.loginPassword(payload);

        if (!r?.mfa?.txId || !r?.mfa?.via) {
          throw new Error("Unable to start verification.");
        }

        setMfaTxId(r.mfa.txId);
        setCodeVia(r.mfa.via === "phone" ? "phone" : "email");
        setStage("code");
        startResendTimer();

        showToast(
          r.mfa.via === "phone"
            ? "We sent a verification code to your phone."
            : "We sent a verification code to your email.",
          "success"
        );
        return;
      }

      if (mode === "email") {
        const { success } = await Api.sendCode(raw.toLowerCase());
        if (!success) throw new Error("Unable to resend verification code.");

        setMfaTxId(null);
        setCodeVia("email");
        startResendTimer();
        showToast("A new verification code has been sent to your email.", "success");
        return;
      }

      if (mode === "phone") {
        const phone = normalizePhone(raw);
        const { success } = await Api.sendCodePayload({ phone });
        if (!success) throw new Error("Unable to resend verification code.");

        setMfaTxId(null);
        setCodeVia("phone");
        startResendTimer();
        showToast("A new verification code has been sent to your phone.", "success");
        return;
      }

      throw new Error("Please enter a valid email address or phone number.");
    } catch (err) {
      console.error(err);
      showToast(friendlyError(err), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    const raw = (emailOrPhone || "").trim();
    const mode = detectMode(raw);

    setResending(true);

    try {
      if (password.trim()) {
        const payload =
          mode === "email"
            ? { email: raw.toLowerCase(), password: password.trim(), rememberMe }
            : { phone: normalizePhone(raw), password: password.trim(), rememberMe };

        const r = await Api.loginPassword(payload);

        if (!r?.mfa?.txId || !r?.mfa?.via) {
          throw new Error("Unable to resend verification code.");
        }

        setMfaTxId(r.mfa.txId);
        setCodeVia(r.mfa.via === "phone" ? "phone" : "email");

        showToast(
          r.mfa.via === "phone"
            ? "A new verification code has been sent to your phone."
            : "A new verification code has been sent to your email.",
          "success"
        );
        startResendTimer();
        return;
      }

      if (mode === "email") {
        const { success } = await Api.sendCode(raw.toLowerCase());
        if (!success) throw new Error("Unable to resend verification code.");

        setMfaTxId(null);
        setCodeVia("email");
        startResendTimer();
        showToast("A new verification code has been sent to your email.", "success");
        return;
      }

      if (mode === "phone") {
        const phone = normalizePhone(raw);
        const { success } = await Api.sendCodePayload({ phone });
        if (!success) throw new Error("Unable to resend verification code.");

        setMfaTxId(null);
        setCodeVia("phone");
        startResendTimer();
        showToast("A new verification code has been sent to your phone.", "success");
        return;
      }

      showToast(
        "Please go back, enter your email or phone number, and request a new code.",
        "error"
      );
    } catch (err) {
      console.error(err);
      showToast(friendlyError(err), "error");
    } finally {
      setResending(false);
    }
  };

  const handleSubmitCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    const raw = (emailOrPhone || "").trim();
    const mode = detectMode(raw);

    try {
      const isPasswordless = !mfaTxId && (mode === "email" || mode === "phone");

      if (stage === "code" && codeVia && !mfaTxId && !isPasswordless) {
        throw new Error("Verification session expired.");
      }

      if (!/^\d{6}$/.test(code.trim())) {
        throw new Error("Please enter the 6-digit verification code.");
      }

      if (mfaTxId) {
        const r = await Api.verifyMfa({ txId: mfaTxId, code: code.trim() });
        if (!r?.success) throw new Error("Verification failed.");

        const customToken = r?.firebase?.customToken;
        if (!customToken) {
          throw new Error("No Firebase custom token returned by server.");
        }

        const fbUser = await signInWithServerCustomToken(customToken);

        const me = await Api.meOptional();
        if (!me?.authenticated) {
          throw new Error("Verification did not complete.");
        }

        clearOtpState();
        clearMfaSessionStorage();
        await afterAuthSuccess(fbUser);
        return;
      }

      if (mode === "email") {
        const verify = await Api.verifyCode(raw.toLowerCase(), code.trim());
        if (!verify?.success) throw new Error("Verification failed.");

        const customToken = verify?.firebase?.customToken;
        if (!customToken) {
          throw new Error("No Firebase custom token returned by server.");
        }

        const fbUser = await signInWithServerCustomToken(customToken);

        clearOtpState();
        clearMfaSessionStorage();
        await afterAuthSuccess(fbUser);
        return;
      }

      if (mode === "phone") {
        const phone = normalizePhone(raw);
        const verify = await Api.verifyCodePayload({ phone, code: code.trim() });
        if (!verify?.success) throw new Error("Verification failed.");

        const customToken = verify?.firebase?.customToken;
        if (!customToken) {
          throw new Error("No Firebase custom token returned by server.");
        }

        const fbUser = await signInWithServerCustomToken(customToken);

        clearOtpState();
        clearMfaSessionStorage();
        await afterAuthSuccess(fbUser);
        return;
      }

      throw new Error("Please enter a valid email address or phone number.");
    } catch (err) {
      clearMfaSessionStorage();

      try {
        await Api.logout();
      } catch {}

      try {
        await auth.signOut().catch(() => {});
      } catch {}

      clearOtpState();
      setStage("credentials");

      console.error(err);
      showToast(friendlyError(err), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProviderLogin = async (provider) => {
    setLoading(true);

    try {
      provider?.setCustomParameters?.({ prompt: "select_account" });

      await auth.setPersistence(
        rememberMe
          ? firebase.auth.Auth.Persistence.LOCAL
          : firebase.auth.Auth.Persistence.SESSION
      );

      try {
        sessionStorage.setItem(MFA_PENDING_KEY, "1");
      } catch {}

      let cred;
      try {
        cred = await auth.signInWithPopup(provider);
      } catch (e) {
        if (String(e?.code || "").includes("popup-blocked")) {
          await auth.signInWithRedirect(provider);
          return;
        }
        throw e;
      }

      const fbUser = cred?.user || auth.currentUser;
      if (!fbUser) {
        throw new Error("Provider sign-in did not return a Firebase user.");
      }

      const idToken = await fbUser.getIdToken(true);
      await auth.signOut().catch(() => {});

      const started = await Api.socialStartMfa({ idToken });
      const txId = started?.mfa?.txId || started?.txId || null;
      const via = started?.mfa?.via || started?.via || null;

      if (!txId || !via) {
        throw new Error("Unable to start verification. Please try again.");
      }

      try {
        sessionStorage.setItem(MFA_TXID_KEY, txId);
        sessionStorage.setItem(MFA_VIA_KEY, via);
      } catch {}

      setMfaTxId(txId);
      setCodeVia(via === "phone" ? "phone" : "email");
      setStage("code");
      startResendTimer();

      showToast(
        via === "phone"
          ? "We sent a verification code to your phone."
          : "We sent a verification code to your email.",
        "success"
      );
    } catch (err) {
      console.error(err);
      showToast(friendlyError(err), "error");
      clearMfaSessionStorage();

      try {
        await auth.signOut().catch(() => {});
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const btnSm = useMemo(
    () => ({ padding: "8px 12px", fontSize: 14, lineHeight: "18px" }),
    []
  );

  const socialBtn = useMemo(() => ({ ...btnSm, width: "48%" }), [btnSm]);

  const linkButtonStyle = {
    background: "transparent",
    border: "none",
    color: "#2457a6",
    padding: 0,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  };

  const startResendTimer = () => {
    setResendCountdown(RESEND_SECONDS);
  };

  const changeIdentifier = () => {
    setStage("credentials");
    clearOtpState();
    clearMfaSessionStorage();
    setResendCountdown(0);
  };

  const isCodeStage = stage === "code";

  return (
    <div className="login-page-wrapper">
      {toast.show && (
        <AlertToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "success" })}
        />
      )}

      <div className="login-page-shell">
        <div className="login-page-container login-page-container--premium auth-container">
          <aside className="login-left login-left--premium">
            <div className="login-left__badge">Secure account access</div>

            <div className="login-brand login-brand--centered">
              <img src="/handshake-logo.png" alt="NexterZone" className="login-logo" />

              <h1 className="login-brand__title">Nexter Zone</h1>
              <p className="slogan">Where value finds its match.</p>
            </div>

            <div className="login-left__content">
              <h2 className="login-left__headline">
                Sign in to continue your marketplace journey.
              </h2>

              <p className="login-left__copy">
                Access saved listings, continue secure offers, track trade activity, and manage your account from one place.
              </p>

              <div className="login-trust-list">
                <div className="login-trust-item login-trust-item--with-icon">
                  <span className="login-trust-icon">✓</span>
                  <span>
                    <span className="login-trust-item__title">Secure verification</span>
                    <span className="login-trust-item__text">Email or phone registration with verification support.</span>
                  </span>
                </div>

                <div className="login-trust-item login-trust-item--with-icon">
                  <span className="login-trust-icon">●</span>
                  <span>
                    <span className="login-trust-item__title">Easy onboarding</span>
                    <span className="login-trust-item__text">Complete setup quickly and continue straight to your preferences.</span>
                  </span>
                </div>

                <div className="login-trust-item login-trust-item--with-icon">
                  <span className="login-trust-icon">↗</span>
                  <span>
                    <span className="login-trust-item__title">Ready to trade</span>
                    <span className="login-trust-item__text">Begin browsing, posting, and managing trades with confidence.</span>
                  </span>
                </div>
              </div>
            </div>
          </aside>

          <section className="login-right login-right--premium">
            <div className="login-panel">
              <div className="login-panel__header">
                <div>
                  <div className="login-panel__eyebrow">
                    {isCodeStage ? "Verification required" : "Welcome back"}
                  </div>
                  <h2>{isCodeStage ? "Enter your code" : "Login"}</h2>
                  <p className="login-panel__subtext">
                    {isCodeStage
                      ? `Enter the 6-digit ${codeVia === "phone" ? "SMS" : "email"} code to complete sign in.`
                      : "Use your email or phone number to continue securely."}
                  </p>
                </div>
              </div>

              {stage === "credentials" && !phoneMode ? (
                <form onSubmit={handleSubmitCredentials} autoComplete="off" className="login-form">
                  <div className="login-field">
                    <label htmlFor="login-identifier">Email or Phone</label>
                    <input
                      id="login-identifier"
                      type="text"
                      placeholder="Enter your email or phone"
                      value={emailOrPhone}
                      onChange={(e) => setEmailOrPhone(e.target.value)}
                      required
                    />
                  </div>

                  <div className="login-field">
                    <label htmlFor="login-password">Password</label>

                    <div className="login-password-wrap">
                      <input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Optional unless using password + code login"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="login-password-input"
                      />

                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M3 3L21 21"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            <path
                              d="M10.58 10.58A2 2 0 0 0 13.42 13.42"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            <path
                              d="M9.88 5.1A10.72 10.72 0 0 1 12 4.89C17.5 4.89 21 9.5 22 12C21.56 13.1 20.61 14.55 19.24 15.79"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.11 6.11C4.05 7.42 2.68 9.55 2 12C3 14.5 6.5 19.11 12 19.11C13.64 19.11 15.09 18.69 16.33 18.02"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M2 12C3 9.5 6.5 4.89 12 4.89C17.5 4.89 21 9.5 22 12C21 14.5 17.5 19.11 12 19.11C6.5 19.11 3 14.5 2 12Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 15A3 3 0 1 0 12 9A3 3 0 0 0 12 15Z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="login-form__row login-form__row--end">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={resettingPassword || loading}
                      style={linkButtonStyle}
                    >
                      {resettingPassword ? "Sending reset link..." : "Forgot password?"}
                    </button>
                  </div>

                  <label className="remember-me-label login-check-row">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>

                  <button type="submit" disabled={loading} className="login-primary-btn" style={btnSm}>
                    {loading ? "Processing..." : "Continue"}
                  </button>
                </form>
              ) : stage === "credentials" && phoneMode ? (
                <form
                  onSubmit={confirmationResult ? handleVerifyPhoneOtp : handleSendPhoneOtp}
                  autoComplete="off"
                  className="login-form login-phone-form"
                >
                  <div className="login-phone-otp-panel">
                    <h3>{confirmationResult ? "Enter verification code" : "Phone OTP sign-in"}</h3>
                    <p className="login-phone-otp-panel__hint">
                      {confirmationResult
                        ? "We sent a 6-digit code to your phone."
                        : "Enter your mobile number and we’ll send a 6-digit verification code."}
                    </p>

                    {!confirmationResult ? (
                      <>
                        <div className="login-field">
                          <label htmlFor="phone-otp-number">Phone number</label>
                          <input
                            id="phone-otp-number"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            placeholder="082 123 4567"
                            value={phoneNumber}
                            onChange={(e) => {
                              setPhoneNumber(formatPhoneForDisplay(e.target.value));
                              setPhoneError("");
                            }}
                            disabled={phoneBusy}
                            required
                            className="login-phone-number-input"
                          />
                        </div>

                        {phoneError && <div className="login-phone-otp-error">{phoneError}</div>}

                        <button
                          type="submit"
                          disabled={phoneBusy || !phoneNumber.trim()}
                          className="login-primary-btn"
                          style={btnSm}
                        >
                          {phoneBusy ? "Sending..." : "Send OTP"}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="login-field">
                          <label htmlFor="phone-otp-code">Verification code</label>
                          <input
                            id="phone-otp-code"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="Enter the 6-digit code"
                            value={otpCode}
                            onChange={(e) => {
                              setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                              setPhoneError("");
                            }}
                            disabled={phoneBusy}
                            required
                          />
                        </div>

                        {phoneError && <div className="login-phone-otp-error">{phoneError}</div>}

                        <button
                          type="submit"
                          disabled={phoneBusy || !/^\d{6}$/.test(otpCode.trim())}
                          className="login-primary-btn"
                          style={btnSm}
                        >
                          {phoneBusy ? "Verifying..." : "Verify and continue"}
                        </button>
                      </>
                    )}

                    <div className="login-phone-otp-actions">
                      <button
                        type="button"
                        onClick={handlePhoneBackToEmail}
                        disabled={phoneBusy}
                        className="login-phone-secondary-btn"
                      >
                        Use email instead
                      </button>
                    </div>

                    <div id="phone-recaptcha-container" />
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmitCode} autoComplete="off" className="login-form">
                  <div className="login-field">
                    <label htmlFor="login-code">Verification code</label>
                    <input
                      id="login-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder={`Enter the 6-digit ${codeVia === "phone" ? "SMS" : "email"} code`}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </div>

                  <div className="login-form__row login-form__row--split">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resending || loading || resendCountdown > 0}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: resendCountdown > 0 ? "#9aa3af" : "#2457a6",
                        padding: 0,
                        cursor: resendCountdown > 0 ? "not-allowed" : "pointer",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {resending
                        ? "Sending new code..."
                        : resendCountdown > 0
                        ? `Resend code in ${resendCountdown}s`
                        : "Resend code"}
                    </button>

                    <button
                      type="button"
                      onClick={changeIdentifier}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#666",
                        padding: 0,
                        cursor: "pointer",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Change email or phone
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || code.trim().length !== 6}
                    className="login-primary-btn"
                    style={{ ...btnSm, marginTop: 12 }}
                  >
                    {loading ? "Verifying..." : "Verify Code & Login"}
                  </button>
                </form>
              )}

              {stage === "credentials" && !phoneMode ? (
                <>
                  <div className="login-divider">
                    <span>{ENABLE_PHONE_LOGIN ? "OR USE A QUICK SIGN-IN OPTION" : "OR"}</span>
                  </div>

                  <div className="login-provider-row" style={{ flexDirection: ENABLE_FACEBOOK_LOGIN ? "row" : "column" }}>
                    <button
                      type="button"
                      onClick={() => handleProviderLogin(googleProvider)}
                      className="provider-btn google"
                      style={{
                        ...socialBtn,
                        width: ENABLE_FACEBOOK_LOGIN ? "48%" : "100%",
                      }}
                      disabled={loading || stage === "code"}
                    >
                      Google
                    </button>
                    {ENABLE_FACEBOOK_LOGIN && (
                      <button
                        type="button"
                        onClick={() => handleProviderLogin(facebookProvider)}
                        className="provider-btn facebook"
                        style={socialBtn}
                        disabled={loading || stage === "code"}
                      >
                        Facebook
                      </button>
                    )}
                  </div>

                  {ENABLE_PHONE_LOGIN ? (
                    <button
                      type="button"
                      onClick={handlePhoneOtpClick}
                      className="login-primary-btn"
                      style={{
                        ...btnSm,
                        marginTop: 12,
                        color: "#ffffff",
                      }}
                      disabled={loading || stage === "code"}
                    >
                      Continue with Phone OTP
                    </button>
                  ) : null}
                </>
              ) : null}

              <p className="switch-mode login-switch-mode" style={{ marginTop: 14 }}>
                Don’t have an account? <Link to="/register">Register</Link>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;